/**
 * RiskAcceptanceService — POA&M / risk acceptance lifecycle (V2-C-19).
 *
 * Immutable rows — corrections mint a new row and mark the prior
 * superseded. Expiry sweep re-opens the register item (V3-0-14).
 *
 * V3-0-14: create/close/expire emit audit events via AuditEventService;
 * runtime rationale validation (MIN_REASON_LEN = 20); expiry flips
 * customer_register_items.status back to 'todo'.
 *
 * D-106-20 (migration 543, ADR-0078 note): an owner / admin's "accept" on a
 * SOC 2 / ISO 42001 Not met point writes a row here in the same batch as the
 * decision (`prepareAcceptance` builds the INSERT; program-entry-service runs
 * it). The row carries `program_*` link columns back to the point and the
 * request entry. Closing or expiring a linked row also ends that request, so
 * the point is Not met (ranked first) again.
 */
import { ACCEPTANCE_MAX_TERM_DAYS } from './acceptance-service.js';
export const MIN_RATIONALE_LEN = 20;
export class RiskAcceptanceValidationError extends Error {
    constructor(message) {
        super(message);
        this.name = 'RiskAcceptanceValidationError';
    }
}
/** V98-7-3 H3: the risk owner can't be the acceptor (403 `risk_owner`). */
export class RiskAcceptanceForbiddenError extends Error {
    code = 'risk_owner';
    constructor(message) {
        super(message);
        this.name = 'RiskAcceptanceForbiddenError';
    }
}
/**
 * Validate an acceptance and build its INSERT (no write). `createAcceptance`
 * runs it alone; D-106-20 runs it in the same batch as the program decision.
 */
export function prepareAcceptance(input) {
    // V98-7-3 H3 (ADR-0046 SoD pair risk_owner ↔ acceptance approver): the owner never accepts their own risk.
    if (input.owner_user_id === input.accepted_by_user_id) {
        throw new RiskAcceptanceForbiddenError('You own this risk, so someone else accepts it (segregation of duties).');
    }
    const trimmed = (input.residual_risk_rationale ?? '').trim();
    // V98-5-5 / D-98-12: an acceptance runs at most 90 days (a missing expiry is refused by the DB; a
    // date already past is simply swept closed by `sweepExpiredAcceptances`).
    if (input.target_closure_at != null) {
        const t = Date.parse(input.target_closure_at);
        if (Number.isNaN(t))
            throw new RiskAcceptanceValidationError('target_closure_at is not a valid date.');
        if (t > Date.now() + ACCEPTANCE_MAX_TERM_DAYS * 86_400_000) {
            throw new RiskAcceptanceValidationError(`An acceptance lasts at most ${ACCEPTANCE_MAX_TERM_DAYS} days; renew it before it ends instead.`);
        }
    }
    if (trimmed.length < MIN_RATIONALE_LEN) {
        throw new RiskAcceptanceValidationError(`Rationale must be at least ${MIN_RATIONALE_LEN} characters (got ${trimmed.length}).`);
    }
    const l = input.program_link;
    return {
        sql: `INSERT INTO risk_acceptances (
       id, workspace_id, profile_id, register_item_id,
       owner_user_id, accepted_by_user_id,
       target_closure_at, residual_risk_rationale,
       control_id, system_id, compensating_controls${l ? ',\n       program_key, program_profile_id, program_period_id, program_objective_id, program_entry_id' : ''}
     ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?${l ? ', ?, ?, ?, ?, ?' : ''})`,
        params: [
            input.id, input.workspace_id, input.profile_id,
            input.register_item_id, input.owner_user_id,
            input.accepted_by_user_id, input.target_closure_at,
            input.residual_risk_rationale,
            input.control_id ?? null, input.system_id ?? null,
            input.compensating_controls?.length ? JSON.stringify(input.compensating_controls) : null,
            ...(l ? [l.program_key, l.profile_id, l.period_id, l.objective_id, l.request_entry_id] : []),
        ],
    };
}
/** The `risk.accepted` audit event for a written acceptance. */
export async function emitAcceptanceCreated(audit, input) {
    await audit.emit({
        workspaceId: input.workspace_id,
        eventType: 'risk.accepted',
        actorType: 'user',
        actorId: input.accepted_by_user_id,
        targetKind: 'risk_acceptance',
        targetId: input.id,
        after: {
            register_item_id: input.register_item_id,
            target_closure_at: input.target_closure_at,
            ...(input.program_link ? { program: input.program_link.program_key, objective_id: input.program_link.objective_id, request_entry_id: input.program_link.request_entry_id } : {}),
        },
        reasonText: input.residual_risk_rationale,
    });
}
export async function createAcceptance(db, audit, input) {
    const stmt = prepareAcceptance(input);
    await db.execute(stmt.sql, stmt.params);
    const result = await db.query(`SELECT * FROM risk_acceptances WHERE id = ? AND workspace_id = ? LIMIT 1`, [input.id, input.workspace_id]);
    const row = result.results[0];
    await emitAcceptanceCreated(audit, input);
    return row;
}
/**
 * D-106-20: end the program risk request a register row came from (the point
 * is Not met again). Runs in the same batch as the close; it matches only the
 * row this close / expiry just wrote, so it is a no-op for unlinked rows.
 */
function endLinkedRequestStatement(acceptanceId, workspaceId, at, by, reason, closedBy) {
    return {
        sql: `UPDATE program_entries SET ended_at = ?, ended_by = ?, end_reason = ?
           WHERE workspace_id = ? AND kind = 'objective_risk_request' AND ended_at IS NULL
             AND entry_id = (SELECT program_entry_id FROM risk_acceptances
                              WHERE id = ? AND workspace_id = ? AND closure_status = 'closed' AND closed_at = ?
                                AND COALESCE(closed_by_user_id, '') = ? AND program_entry_id IS NOT NULL)`,
        params: [at, by, reason, workspaceId, acceptanceId, workspaceId, at, closedBy ?? ''],
    };
}
/**
 * Close an open acceptance in this workspace. V98-7-3: scoped by workspace
 * (it was by id alone — a cross-tenant write); returns false when no open
 * acceptance with that id exists here (the route answers 404).
 */
export async function closeAcceptance(db, audit, acceptanceId, closedByUserId, workspaceId) {
    const now = new Date().toISOString();
    // D-106-20: the close and the end of a linked program request are one batch.
    const [res] = await db.batch([
        {
            sql: `UPDATE risk_acceptances
        SET closure_status = 'closed',
            closed_at = ?,
            closed_by_user_id = ?
      WHERE id = ? AND workspace_id = ? AND closure_status = 'open'`,
            params: [now, closedByUserId, acceptanceId, workspaceId],
        },
        endLinkedRequestStatement(acceptanceId, workspaceId, now, closedByUserId, 'acceptance_closed', closedByUserId),
    ]);
    if (res?.meta?.changes === 0)
        return false;
    if (res?.meta?.changes === undefined) {
        // Adapters without a change count: confirm this call closed it.
        const r = await db.query(`SELECT id FROM risk_acceptances WHERE id = ? AND workspace_id = ? AND closure_status = 'closed' AND closed_at = ? AND closed_by_user_id = ?`, [acceptanceId, workspaceId, now, closedByUserId]);
        if (!r.results[0])
            return false;
    }
    await audit.emit({
        workspaceId,
        eventType: 'risk.acceptance_closed',
        actorType: 'user',
        actorId: closedByUserId,
        targetKind: 'risk_acceptance',
        targetId: acceptanceId,
        after: { closure_status: 'closed', closed_at: now },
    });
    return true;
}
/**
 * D-106-20: close the open register rows of one program request (a withdraw, or a decline after
 * an acceptance), as a statement the caller runs in the same batch as ending / deciding the
 * request. The caller emits `emitAcceptancesClosed` after the batch commits.
 */
export function closeProgramRequestAcceptancesStatement(workspaceId, requestEntryId, at, closedByUserId) {
    return {
        sql: `UPDATE risk_acceptances SET closure_status = 'closed', closed_at = ?, closed_by_user_id = ?
           WHERE workspace_id = ? AND program_entry_id = ? AND closure_status = 'open'`,
        params: [at, closedByUserId, workspaceId, requestEntryId],
    };
}
/** The open register row ids of one program request (read before the closing batch). */
export async function openProgramRequestAcceptances(db, workspaceId, requestEntryId) {
    try {
        return (await db.query(`SELECT id FROM risk_acceptances WHERE workspace_id = ? AND program_entry_id = ? AND closure_status = 'open' ORDER BY id`, [workspaceId, requestEntryId])).results.map((r) => r.id);
    }
    catch {
        return []; // migration 543 not on this plane
    }
}
/** `risk.acceptance_closed` for rows a program batch closed. */
export async function emitAcceptancesClosed(audit, workspaceId, ids, closedByUserId, at, reason) {
    for (const id of ids) {
        await audit.emit({
            workspaceId, eventType: 'risk.acceptance_closed', actorType: 'user', actorId: closedByUserId,
            targetKind: 'risk_acceptance', targetId: id, after: { closure_status: 'closed', closed_at: at, reason },
        });
    }
}
export async function supersedeAcceptance(db, workspaceId, oldId, newId) {
    await db.execute(`UPDATE risk_acceptances
        SET closure_status = 'superseded',
            superseded_by_id = ?
      WHERE id = ? AND workspace_id = ? AND closure_status = 'open'`, [newId, oldId, workspaceId]);
}
export async function getActiveAcceptance(db, workspaceId, registerItemId) {
    const result = await db.query(`SELECT * FROM risk_acceptances
      WHERE workspace_id = ? AND register_item_id = ?
        AND closure_status = 'open'
      ORDER BY accepted_at DESC
      LIMIT 1`, [workspaceId, registerItemId]);
    return result.results[0] ?? null;
}
export async function listAcceptances(db, workspaceId, opts) {
    const clauses = ['workspace_id = ?'];
    const params = [workspaceId];
    if (opts?.status) {
        clauses.push('closure_status = ?');
        params.push(opts.status);
    }
    const result = await db.query(`SELECT * FROM risk_acceptances
      WHERE ${clauses.join(' AND ')}
      ORDER BY accepted_at DESC`, params);
    return result.results;
}
export async function sweepExpiredAcceptances(db, audit) {
    const now = new Date().toISOString();
    const expired = await db.query(`SELECT id, register_item_id, workspace_id, profile_id
       FROM risk_acceptances
      WHERE closure_status = 'open'
        AND target_closure_at <= ?`, [now]);
    const expiredIds = [];
    for (const row of expired.results) {
        await db.batch([
            {
                sql: `UPDATE risk_acceptances
          SET closure_status = 'closed',
              closed_at = ?
        WHERE id = ? AND workspace_id = ?`,
                params: [now, row.id, row.workspace_id],
            },
            // D-106-20: a lapsed program acceptance ends its request (the point is Not met again).
            endLinkedRequestStatement(row.id, row.workspace_id, now, 'system:risk_acceptance_expiry', 'acceptance_expired', null),
        ]);
        await db.execute(`UPDATE customer_register_items
          SET status = 'todo',
              updated_at = ?
        WHERE profile_id = ? AND risk_item_id = ?
          AND status IN ('done', 'not_applicable')`, [now, row.profile_id, row.register_item_id]);
        await audit.emit({
            workspaceId: row.workspace_id,
            eventType: 'risk.acceptance_expired',
            actorType: 'system',
            actorId: 'sweep:risk_acceptance_expiry',
            targetKind: 'risk_acceptance',
            targetId: row.id,
            after: {
                register_item_id: row.register_item_id,
                reopened: true,
            },
        });
        expiredIds.push(row.id);
    }
    return expiredIds;
}
//# sourceMappingURL=risk-acceptance-service.js.map